// S-MKT-PRO-W9 — buyer CART client state (a tiny module-level store + a React hook), the
// same shape wishlist-state.ts uses. The cart is OWN-ONLY (the API scopes to the caller), so
// ONE in-memory set of "course ids in my cart" + a count backs every «افزودن به سبد» button
// on the catalog cards, the course-detail buy box, the header badge and the cart page.
//
// Behaviour: OPTIMISTIC add/remove (flip the set immediately) + reconcile from the server
// response (every cart mutation returns the whole cart, so the badge is never stale). On
// error it reverts and rethrows so the caller can toast.
//
// A guest (no session) has no cart; the hook reports `ready=false` + an empty set so the
// button can fall back to routing to sign-in.

import React from "react";

import { marketApi } from "../../api/endpoints-core.js";

/** One cart line, priced LIVE by the server (a line stores no price of its own). */
export interface CartLine {
  id: string;
  marketCourseId: string;
  title: string | null;
  coverImageUrl: string | null;
  level: string | null;
  pricingModel: string | null;
  listPriceRial: number;
  pricePaidRial: number;
  discountRial: number;
  couponId: string | null;
  couponCode: string | null;
  /** Persian reason this line cannot be bought right now (null = ready). */
  blockedReason: string | null;
  addedAt: string;
}

export interface CartPayload {
  items: CartLine[];
  count: number;
  totalListRial: number;
  totalPayableRial: number;
  totalDiscountRial: number;
  blockedCount: number;
  walletPayEnabled: boolean;
  walletBalanceRial: number;
}

// ---------------------------------------------------------------------------
// Module-level store (shared across every mounted consumer in the SPA session).
// ---------------------------------------------------------------------------
let ids = new Set<string>();
let loaded = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = (): void => {
  for (const l of listeners) l();
};

/**
 * Reconcile the shared store from ANY cart payload the server returned (list, add, remove,
 * coupon, checkout). Exported so the cart page keeps the header badge in sync for free.
 */
export const syncCart = (cart: CartPayload | null | undefined): void => {
  ids = new Set((cart?.items ?? []).map((i) => i.marketCourseId));
  loaded = true;
  emit();
};

/** Fetch the caller's cart once (deduped). Best-effort: a failure leaves it empty. */
const fetchOnce = async (): Promise<void> => {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      syncCart((await marketApi.cart()) as CartPayload);
    } catch {
      loaded = true; // mark ready so the UI stops waiting; the set stays as-is
      emit();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

/** Force a re-read (used after a checkout, or by the cart page on mount). */
export const refreshCart = async (): Promise<void> => {
  inflight = null;
  await fetchOnce();
};

// ---------------------------------------------------------------------------
// Hook — the shared in-cart set + optimistic add/remove.
// ---------------------------------------------------------------------------
export interface UseCart {
  /** true once the first fetch has resolved (or failed). */
  ready: boolean;
  /** how many lines are in the cart (the header badge). */
  count: number;
  inCart: (courseId: string) => boolean;
  /** Add a course. Resolves to the server's cart; reverts + rethrows on failure. */
  add: (courseId: string) => Promise<void>;
  /** Remove a course. Resolves to the server's cart; reverts + rethrows on failure. */
  remove: (courseId: string) => Promise<void>;
}

/**
 * @param enabled  pass false for a guest (no session) — the hook then never fetches and the
 *                 mutations reject (the caller should route to /login instead).
 */
export const useCart = (enabled: boolean): UseCart => {
  const [, force] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    if (!enabled) return;
    const l = () => force();
    listeners.add(l);
    if (!loaded) void fetchOnce();
    return () => {
      listeners.delete(l);
    };
  }, [enabled]);

  const inCart = React.useCallback((courseId: string) => ids.has(courseId), []);

  const mutate = React.useCallback(
    async (courseId: string, wantIn: boolean): Promise<void> => {
      if (!enabled) throw new Error("not-authenticated");
      const before = ids;
      const next = new Set(ids);
      if (wantIn) next.add(courseId);
      else next.delete(courseId);
      ids = next;
      emit();
      try {
        const res = (await (wantIn
          ? marketApi.addToCart(courseId)
          : marketApi.removeFromCart(courseId))) as { cart?: CartPayload } & CartPayload;
        // add() answers { ok, id, added, cart }; remove() answers the cart itself.
        syncCart(res?.cart ?? res);
      } catch (err) {
        ids = before;
        emit();
        throw err;
      }
    },
    [enabled],
  );

  const add = React.useCallback((courseId: string) => mutate(courseId, true), [mutate]);
  const remove = React.useCallback((courseId: string) => mutate(courseId, false), [mutate]);

  return { ready: loaded, count: ids.size, inCart, add, remove };
};
